import { api, LightningElement, wire } from "lwc";

import {
  LearningSummaryConnectionType,
  LearningsFragment,
  LearningSummaryType,
  LearningUuidInputType,
  TrailmixUuidInputType,
} from "@devforce/trailhead-client-gql";
import { getLocale } from "@devforce/trailhead-client-locale";

import { QueryAdapter, QueryAdapterValue, gql } from "common/graphqlAdapter";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";

import type { ActionConfig } from "common/contentActions";
import type { ContentSummary } from "common/contentSummary";
import { BooleanAttr } from "common/helpers";

import { showToast } from "tds/toaster";

import * as locales from "./locales";
import type { LearningKindCollectionType } from "common/learning";

type Option = {
  handler: () => void;
  subtitle: string;
  title: string;
};

export type UserDetailsResponseType = {
  id: string;
  userInfo: {
    trailblazerSlug: string;
  };
};

export default class extends LightningElement {
  @api config: ActionConfig = null!;
  @api open: BooleanAttr = false;
  @api summary: ContentSummary = null!;

  private selectedTrailmix: LearningSummaryType | null = null;
  private userDetails: UserDetailsResponseType | null = null;
  private options: Option[] | null = null;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  private get labelOptions() {
    return {
      add_to_trailmix_success: {
        trailmixName: this.selectedTrailmix ? this.selectedTrailmix.label : "",
        trailmixUrl: this.selectedTrailmix ? this.selectedTrailmix.url : "",
      },
    };
  }
  private get contentType() {
    switch (this.config.learning.type as LearningKindCollectionType) {
      case "MODULE":
        return encodeURIComponent("Th::Module");
      case "PROJECT":
        return encodeURIComponent("Th::Project");
      case "TRAIL":
        return encodeURIComponent("Th::Trail");
      default:
        return "";
    }
  }

  private get newUrl() {
    if (this.userDetails) {
      const { config, contentType } = this;
      const { learning } = config;
      const username = this.userDetails?.userInfo.trailblazerSlug;
      return `/${getLocale()}/users/${username}/trailmixes/new?contentType=${contentType}&apiName=${
        learning.name
      }`;
    }
    return null;
  }

  private get stencils() {
    return [1, 2, 3];
  }

  private handleRequestClose() {
    this.dispatchEvent(new CustomEvent("requestclose"));
  }

  private handleSelect(trailmix: LearningSummaryType) {
    const trailmixData: TrailmixUuidInputType = {
      uuid: trailmix.id,
    };
    const learningData: LearningUuidInputType = {
      uuid: this.summary.id,
      type: this.config.learning.type,
    };
    this.selectedTrailmix = trailmix;

    void this.addToTrailmix(learningData, trailmixData);
  }

  private async addToTrailmix(
    learning: LearningUuidInputType,
    trailmix: TrailmixUuidInputType
  ) {
    let { getClient, gql } = await import("@devforce/trailhead-client-apollo");
    try {
      await getClient().mutate({
        mutation: gql`
          mutation AddToTrailmix(
            $learning: LearningUuidInput!
            $trailmix: TrailmixUuidInput!
          ) {
            addToTrailmix(input: { learning: $learning, trailmix: $trailmix }) {
              __typename
              success
            }
          }
        `,
        variables: {
          learning: learning,
          trailmix: trailmix,
        },
      });
      showToast({
        unsafeMessage: this.labels.add_to_trailmix_success,
        variant: "success",
      });
    } catch {
      showToast({
        message: this.labels.add_to_trailmix_duplicate,
        variant: "error",
      });
    }
  }

  @wire(QueryAdapter, { options: "$userQueryOptions" })
  private onUserResult(
    result: QueryAdapterValue<{ getUserProfile: UserDetailsResponseType }>
  ) {
    if (result.error) {
      console.log(result.error);
    } else if (result.errors) {
      console.log(result.errors);
    } else if (result.data) {
      this.userDetails = result.data.getUserProfile;
    }
  }

  private get userQueryOptions() {
    return {
      query: gql`
        query UserProfile {
          getUserProfile {
            __typename
            id
            userInfo {
              trailblazerSlug
            }
          }
        }
      `,
    };
  }

  @wire(QueryAdapter, { options: "$queryOptions" })
  private onResult(
    result: QueryAdapterValue<{ findTrailmixes: LearningSummaryConnectionType }>
  ) {
    if (result.error) {
      console.log(result.error);
    } else if (result.errors) {
      console.log(result.errors);
    } else if (result.data) {
      this.options = result.data.findTrailmixes
        .edges!.map((edge) => edge!.node!)
        .map((learning) => {
          return {
            handler: () => {
              this.dispatchEvent(
                new CustomEvent("select", { detail: learning })
              );
              this.handleSelect(learning);
            },
            subtitle: learning.label,
            title: learning.label,
          };
        });
    }
  }

  private get queryOptions() {
    if (this.userDetails) {
      const { id } = this.userDetails;
      return {
        query,
        variables: {
          creatorId: id,
        },
      };
    }
    return null;
  }
}

export const query = gql`
  query FindTrailmixes($first: Int = 29, $after: String, $creatorId: Uuid) {
    findTrailmixes(
      first: $first
      after: $after
      filter: { creatorId: { eq: $creatorId }, crossOrg: { eq: true } }
    ) {
      ...Learnings
    }
  }
  ${LearningsFragment}
`;
